import type { ConnectingDelayJudgementMode, ConnectingDirectionEntry } from '../../../api/types';
import type { RouteMaster } from '../../../data/routes';
import { sortStopsBySequence, type RouteStop } from '../../../data/routeStops';
import { StopSelect } from '../parts/StopSelect';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 方面ぶんのブロック。
 * `ConnectingDirectionsSection` の 1 方面を編集する。
 *
 * 判定モードによる項目表示切替:
 *   - whole_line       : direction_id (判定用) / 区間 (start/end_stop_id) 非表示。
 *                        direction_name (案内文面用) のみ入力。
 *   - section_scheduled: direction_id + 区間 (start/end_stop_id) + direction_name。
 *   - transfer_aware   : direction_id + direction_name (区間は非表示)。
 */
export function ConnectingDirectionBlock({
  lineIndex,
  directionIndex,
  direction,
  routeId,
  judgementMode,
  routes,
  routeStops,
  canRemove,
  isFirst,
  isLast,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
}: {
  lineIndex: number;
  directionIndex: number;
  direction: ConnectingDirectionEntry;
  routeId: string;
  judgementMode: ConnectingDelayJudgementMode;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  canRemove: boolean;
  isFirst: boolean;
  isLast: boolean;
  onChange: (next: ConnectingDirectionEntry) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}): React.JSX.Element {
  const directionId = direction.direction_id ?? '';
  const directionName = direction.direction_name ?? '';
  const startStopId = direction.start_stop_id ?? '';
  const endStopId = direction.end_stop_id ?? '';

  const isWholeLineMode = judgementMode === 'whole_line';
  const isSectionScheduledMode = judgementMode === 'section_scheduled';

  // route_id に紐づく方面選択肢を生成 (part1 と同じ流儀)。
  const currentRoute = routes.find((r) => r.route_id === routeId);
  const directionOptions = (currentRoute?.directions ?? []).map((d, i) => ({
    value: typeof d.direction_id === 'string' && d.direction_id !== '' ? d.direction_id : String(i),
    label: d.direction_name,
  }));
  const isUnknownDirection =
    directionId !== '' && !directionOptions.some((o) => o.value === directionId);

  // 区間駅 (start/end_stop_id) の選択肢は route_id の駅一覧から。
  const stops = sortStopsBySequence(routeId ? routeStops[routeId] : []);

  /** 方面を patch で更新 */
  const emit = (patch: Partial<ConnectingDirectionEntry>): void => {
    onChange({ ...direction, ...patch });
  };

  const testIdSuffix = `${lineIndex}-${directionIndex}`;

  return (
    <div
      className="connecting-direction-block"
      data-testid={`connecting-direction-block-${testIdSuffix}`}
    >
      {/* ヘッダ: ラベル + 操作ボタン (↑↓ / − 削除) */}
      <div
        className="connecting-direction-block-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          marginBottom: 4,
          flexWrap: 'wrap',
        }}
      >
        <strong>方面 #{directionIndex + 1}</strong>
        <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
          <button
            type="button"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を上へ`}
            data-testid={`connecting-direction-move-up-${testIdSuffix}`}
            disabled={isFirst}
            onClick={onMoveUp}
          >
            ↑
          </button>
          <button
            type="button"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を下へ`}
            data-testid={`connecting-direction-move-down-${testIdSuffix}`}
            disabled={isLast}
            onClick={onMoveDown}
          >
            ↓
          </button>
          {canRemove ? (
            <button
              type="button"
              className="danger"
              aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} を削除`}
              data-testid={`connecting-direction-remove-${testIdSuffix}`}
              onClick={onRemove}
            >
              − 削除
            </button>
          ) : null}
        </div>
      </div>

      {/* 判定用の方面 direction_id (whole_line では非表示) */}
      {!isWholeLineMode ? (
        <label>
          <span>方面 (判定用・任意)</span>
          <select
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 判定用`}
            data-testid={`connecting-direction-id-${testIdSuffix}`}
            value={directionId}
            disabled={!routeId}
            onChange={(e) => emit({ direction_id: e.target.value })}
          >
            <option value="">(指定なし)</option>
            {directionOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
            {isUnknownDirection ? (
              <option value={directionId}>{directionId} (マスタ未登録)</option>
            ) : null}
          </select>
        </label>
      ) : null}

      {/* 案内文面用の direction_name (必須) */}
      <label>
        <span className="required">方面 (案内文面用)</span>
        <div className="direction-name-input-row">
          <input
            type="text"
            aria-label={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 案内文面`}
            data-testid={`connecting-direction-name-${testIdSuffix}`}
            required
            value={directionName}
            placeholder="例: 新宿"
            onChange={(e) => emit({ direction_name: e.target.value })}
          />
          <span
            className="direction-name-suffix"
            data-testid={`connecting-direction-name-suffix-${testIdSuffix}`}
            aria-hidden="true"
          >
            方面
          </span>
        </div>
      </label>
      <p className="hint" style={{ margin: '4px 0' }}>
        放送文の <code>{'{direction_name}'}</code> プレースホルダに展開されます (例: 「新宿」→
        「新宿方面は5分遅れ」)。必ず入力してください。
      </p>

      {/* section_scheduled モードでは区間 (開始/終了駅) を方面ごとに指定 */}
      {isSectionScheduledMode ? (
        <>
          <div style={{ display: 'flex', gap: 8 }}>
            <label style={{ flex: 1 }}>
              <span className="required">遅延判定開始駅</span>
              <StopSelect
                ariaLabel={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 開始駅`}
                testId={`connecting-direction-start-stop-${testIdSuffix}`}
                value={startStopId}
                stops={stops}
                disabled={!routeId}
                onChange={(stopId) => emit({ start_stop_id: stopId })}
              />
            </label>
            <label style={{ flex: 1 }}>
              <span className="required">遅延判定終了駅</span>
              <StopSelect
                ariaLabel={`接続線#${lineIndex + 1} 方面#${directionIndex + 1} 終了駅`}
                testId={`connecting-direction-end-stop-${testIdSuffix}`}
                value={endStopId}
                stops={stops}
                disabled={!routeId}
                onChange={(stopId) => emit({ end_stop_id: stopId })}
              />
            </label>
          </div>
          <p className="hint" style={{ margin: '4px 0' }}>
            方面ごとに区間を指定します。区間内に定刻在線している便の遅延で判定します。
          </p>
        </>
      ) : null}
    </div>
  );
}
